import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, FormControl, InputLabel,
  MenuItem, Select, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import SaveIcon from '@mui/icons-material/Save';
import { buildingApi } from './BuildingApi';
import LocationMap from '../tools/LocationMap';
import * as ETVConstants from '../ETVConstants';
import ETVActionButton from '../tools/ETVActionButton';
import ETVPage from '../tools/ETVPage';
import ETVPopup from '../tools/ETVPopup';

const LOCATION_TYPES = [
  [0, 'undefined'], [100, 'location_type_hotel_5_star'], [101, 'location_type_hotel_4_star'],
  [102, 'location_type_hotel_3_star'], [103, 'location_type_hotel_others'],
  [200, 'location_type_mall_luxury'], [201, 'location_type_mall_standard'],
  [300, 'location_type_office_high_rise'], [301, 'location_type_office_mid_rise'],
  [302, 'location_type_office_low_rise'], [400, 'location_type_residential_condo'],
  [401, 'location_type_residential_hdb'], [501, 'location_type_restaurant'],
  [502, 'location_type_cafe'], [600, 'location_type_hospital'],
  [700, 'location_type_university'],
];

const NEW_LOCATION = {
  location_id: 0, location_type: 0, name: '', address: '', zip: '', city: '',
  country: '', country_iso: '', info: '', latitude: '', longitude: '', contact_phone: '', contact_email: '',
};

const COUNTRIES = [
  ['US', 'United States'], ['GB', 'United Kingdom'], ['CA', 'Canada'], ['AU', 'Australia'],
  ['DE', 'Germany'], ['FR', 'France'], ['IN', 'India'], ['CN', 'China'], ['JP', 'Japan'],
  ['SG', 'Singapore'], ['NL', 'Netherlands'], ['KR', 'South Korea'], ['SE', 'Sweden'],
  ['IT', 'Italy'], ['ES', 'Spain'], ['BR', 'Brazil'], ['MX', 'Mexico'], ['CH', 'Switzerland'],
  ['ZA', 'South Africa'], ['AE', 'United Arab Emirates'], ['NZ', 'New Zealand'],
  ['DK', 'Denmark'], ['NO', 'Norway'], ['FI', 'Finland'], ['HK', 'Hong Kong'],
  ['AR', 'Argentina'], ['PL', 'Poland'], ['ID', 'Indonesia'], ['TR', 'Turkey'], ['RU', 'Russia'],
  ['SA', 'Saudi Arabia'], ['IE', 'Ireland'], ['PH', 'Philippines'], ['TH', 'Thailand'],
  ['MY', 'Malaysia'], ['VN', 'Vietnam'], ['CL', 'Chile'], ['CO', 'Colombia'], ['IL', 'Israel'],
  ['PT', 'Portugal'], ['AT', 'Austria'], ['BE', 'Belgium'], ['CZ', 'Czech Republic'],
  ['HU', 'Hungary'], ['LU', 'Luxembourg'],
].map(([code, name]) => ({ code, name }));

const countryOption = (form) => COUNTRIES.find(
  item => item.code === form?.country_iso || item.name === form?.country,
);

export default function LocationDefinition({ user_id }) {
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = (await buildingApi('admin_list_locations')).list || [];
      setLocations(list);
      if (list.length === 1) {
        const locationId = list[0].location_id;
        setSelectedId(locationId);
        const data = await buildingApi('admin_get_location', { query: { location_id: locationId } });
        setForm(data.location || null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const selectLocation = async (id) => {
    setIsAdding(false);
    setSelectedId(id);
    setError('');
    setSuccess('');
    setForm(null);
    if (!id) return;
    setLoading(true);
    try {
      const data = await buildingApi('admin_get_location', { query: { location_id: id } });
      setForm(data.location || null);
    } catch (err) {
      setError(err.message || 'Failed to load location information');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!form?.name?.trim()) { setError('Location name is required'); return; }
    if (form.latitude === '' || form.longitude === '') { setError('Select GPS coordinates from the map'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const isNew = !form.location_id;
      const payload = isNew ? { ...form, owner_user_id: Number(user_id) } : form;
      const result = await buildingApi(isNew ? 'admin_create_location' : 'admin_update_location', {
        method: 'POST', body: JSON.stringify(payload),
      });
      const locationId = isNew ? result.location_id : form.location_id;
      setIsAdding(false);
      setSelectedId(locationId);
      setSuccess(isNew ? 'Location created.' : 'Location information updated.');
      await loadLocations();
      const data = await buildingApi('admin_get_location', { query: { location_id: locationId } });
      setForm(data.location || null);
    } catch (err) {
      setError(err.message || 'Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = 'text') => (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={form?.[key] ?? ''}
      onChange={(event) => setForm({ ...form, [key]: event.target.value })}
    />
  );

  const setLocationGPS = (latitude, longitude) => {
    setForm(current => ({ ...current, latitude, longitude }));
  };

  const addLocation = () => {
    setIsAdding(true);
    setSelectedId('');
    setForm({ ...NEW_LOCATION });
    setError('');
    setSuccess('');
  };

  const cancelAddLocation = () => {
    setIsAdding(false);
    setForm(null);
    setError('');
    setSuccess('');
    if (locations.length === 1) selectLocation(locations[0].location_id);
  };

  const deleteLocation = async () => {
    if (!form?.location_id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await buildingApi('admin_delete_location', {
        method: 'POST', body: JSON.stringify({ location_id: form.location_id }),
      });
      setDeleteOpen(false);
      setSelectedId('');
      setForm(null);
      setSuccess('Location deleted.');
      await loadLocations();
    } catch (err) {
      setError(err.message || 'Failed to delete location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ETVPage
      width="standard"
      icon={<LocationCityIcon fontSize="large" />}
      title={ETVConstants.trans('location_definition') || 'Location definition'}
      description={isAdding
        ? 'Enter the details for the new location.'
        : locations.length === 1
          ? 'Define or update the location information.'
          : 'Select one of your existing buildings to define or update its location information.'}
      loading={loading}
      topActions={(
        <>
          {isAdding ? (
            <ETVActionButton action="cancel" label="Cancel new location" onClick={cancelAddLocation} />
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={addLocation}>Add new location</Button>
          )}
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!isAdding && locations.length > 1 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="location-select-label">Building</InputLabel>
          <Select
            labelId="location-select-label"
            value={selectedId}
            label="Building"
            onChange={(event) => selectLocation(event.target.value)}
          >
            {locations.map(item => (
              <MenuItem key={item.location_id} value={item.location_id}>{item.name} - {item.address}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {form && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {field('name', 'Name')}
          <FormControl fullWidth>
            <InputLabel id="location-type-label">Location type</InputLabel>
            <Select
              labelId="location-type-label"
              label="Location type"
              value={Number(form.location_type ?? 0)}
              onChange={(event) => setForm({ ...form, location_type: event.target.value })}
            >
              {LOCATION_TYPES.map(([value, label]) => (
                <MenuItem key={value} value={value}>{ETVConstants.trans(label)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {field('address', 'Address')}
          {field('zip', 'ZIP / postal code')}
          {field('city', 'City')}
          <Autocomplete
            options={COUNTRIES}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            value={countryOption(form) || null}
            onChange={(event, value) => {
              setForm({
                ...form,
                country: value?.name || '',
                country_iso: value?.code || '',
              });
            }}
            renderInput={(params) => <TextField {...params} label="Country" />}
          />
          <TextField fullWidth disabled label="Latitude" value={form.latitude ?? ''} />
          <TextField fullWidth disabled label="Longitude" value={form.longitude ?? ''} />
          <Box sx={{ gridColumn: { md: '1 / -1' } }}>
            <Button variant="outlined" startIcon={<MapIcon />} onClick={() => setMapOpen(true)}>
              Select GPS coordinates on map
            </Button>
          </Box>
          {field('contact_email', 'Building management contact email', 'email')}
          {field('contact_phone', 'Building contact phone')}
          <Box sx={{ gridColumn: { md: '1 / -1' } }}>{field('info', 'Information')}</Box>
          <Box sx={{ gridColumn: { md: '1 / -1' }, display: 'flex', justifyContent: 'space-between' }}>
            {form.location_id ? (
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
                Delete location
              </Button>
            ) : <span />}
            <ETVActionButton
              action={form.location_id ? 'update' : 'save'}
              startIcon={<SaveIcon />}
              label={form.location_id ? 'Save' : 'Create location'}
              loading={saving}
              onClick={save}
            />
          </Box>
        </Box>
      )}

      {!loading && locations.length === 0 && (
        <Alert severity="info">No buildings are defined for this user.</Alert>
      )}

      <ETVPopup
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        width="wide"
        icon={<MapIcon />}
        title="Select GPS coordinates"
        description="Choose the exact building position on the map."
        contentSx={{ p: 0 }}
        bottomActions={(
          <>
            <Typography sx={{ mr: 'auto' }} variant="body2">
              {form?.latitude || '-'}, {form?.longitude || '-'}
            </Typography>
            <ETVActionButton action="update" label="Use coordinates" onClick={() => setMapOpen(false)} />
          </>
        )}
      >
        {form && (
          <LocationMap
            lat={Number(form.latitude) || undefined}
            lgt={Number(form.longitude) || undefined}
            setLocationGPS={setLocationGPS}
          />
        )}
      </ETVPopup>

      <ETVPopup
        open={deleteOpen}
        onClose={() => !saving && setDeleteOpen(false)}
        width="small"
        icon={<DeleteIcon color="error" />}
        title="Delete location?"
        description="Please confirm this permanent action."
        bottomActions={(
          <>
            <ETVActionButton action="cancel" disabled={saving} onClick={() => setDeleteOpen(false)} />
            <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={deleteLocation} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete location'}
            </Button>
          </>
        )}
      >
        <Typography>
          This permanently deletes “{form?.name}”. Deletion may be refused if devices or other records still use this location.
        </Typography>
      </ETVPopup>
    </ETVPage>
  );
}
